import React, { useState, type FC, type ReactNode } from "react";
import {
  LeftOutlined,
  EditOutlined,
  StopOutlined,
  CaretRightOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { Modal, Spin } from "antd";
import { Button, DataTable, Form, Input, StatusBadge } from "../../../../components/shared";
import FeatureFlagGuard from "../../../FeatureFlagGuard/FeatureFlagGuard";
import { SIDER_TABS } from "../../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../../utils/NavigationUtils";
import { convertToUserTimezone } from "../../../../utils/dateService";
import { formatWholeNumberInput } from "../../../CommonComponents/FormattedInput/FormattedInput";
import { useGetSegment } from "../../../../queries/dynamicBonuses/useGetSegment";
import { useUpdateSegment } from "../../../../queries/dynamicBonuses/useUpdateSegment";
import { toSegmentIn } from "../../../../queries/dynamicBonuses/toWriteBody";
import {
  useSegmentTransition,
  type SegmentTransitionAction,
} from "../../../../queries/dynamicBonuses/useSegmentTransition";
import { useListSegmentMembers } from "../../../../queries/dynamicBonuses/useListSegmentMembers";
import { useListCampaigns } from "../../../../queries/dynamicBonuses/useListCampaigns";
import { useDynamicBonusEvents } from "../../../../queries/dynamicBonuses/useDynamicBonusEvents";
import SegmentRulesEditor from "./SegmentRulesEditor";
import { campaignsUsingSegment } from "./campaignsUsingSegment";
import { segmentStatusBadgeTone, LOOKBACK_WINDOW_DAYS_LABEL, segmentTriggerDisplayLabel } from "../constants";
import { displayNameFor } from "../eventCatalog";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { usePageTitle } from "../../../CommonComponents/Layout/PageTitleContext";
import type {
  SegmentOut,
  MembershipRuleIn,
  MembershipRuleOut,
  FieldCheckIn,
  FieldOperator,
  SegmentIn,
  SegmentMember,
  EventCategoryGroup,
} from "../types";
import listStyles from "../../../Affiliates/Affiliates.module.css";
import wizardStyles from "../../../Affiliates/NewAffiliate/NewAffiliate.module.css";
import styles from "../stylesDynamicBonuses.module.css";

const EMPTY = "—";

const fmt = (iso: string | null | undefined) =>
  iso ? convertToUserTimezone(iso, "YYYY-MM-DD HH:mm") : EMPTY;

const formatCheckValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

/**
 * Best-effort inverse of `formatCheckValue` for a field check the user
 * genuinely typed into the Value input (a brand-new check, or an existing
 * one whose displayed value was actually edited — see `resolveCheckValue`,
 * which reuses the original typed value verbatim for anything untouched).
 * `in` is rendered as a comma-joined list, so it's split back into an
 * array. Everything else round-trips through `JSON.parse` so a
 * `formatCheckValue`-stringified object or number (`{"tier":1}` -> object,
 * `"100"` -> number) comes back typed instead of staying a string; plain
 * text that isn't valid JSON (e.g. `"MA"`) falls back to the raw string.
 */
const parseCheckValue = (operator: FieldOperator, value: unknown): unknown => {
  const raw = String(value ?? "");
  if (operator === "in") {
    return raw
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

/**
 * Resolves the value to persist for a single field check on save. If the
 * check existed before editing (matched by position within the rule) and
 * its field/operator/displayed-value are all still exactly what they were,
 * the original's typed value (array, object, number, whatever) is reused
 * verbatim — closing the hole where reconstructing every check from its
 * plain-text Value input on *any* save (even a name-only edit) silently
 * flattened untouched object/number values to strings. Only a check that
 * was actually added or edited gets re-parsed from its string.
 */
const resolveCheckValue = (
  fc: { field?: string; operator?: FieldOperator; value?: unknown },
  originalCheck: FieldCheckIn | undefined,
): unknown => {
  const isUnchanged =
    originalCheck != null &&
    originalCheck.field === fc.field &&
    originalCheck.operator === fc.operator &&
    formatCheckValue(originalCheck.value) === fc.value;
  return isUnchanged ? originalCheck.value : parseCheckValue(fc.operator!, fc.value);
};

const describeCheck = (fc: FieldCheckIn): string => {
  if (fc.operator === "exists") return `${fc.field} exists`;
  return `${fc.field} ${fc.operator} ${formatCheckValue(fc.value)}`.trim();
};

const eventLabel = (
  rule: { event_key?: string | null; trigger_key?: string },
  groups: EventCategoryGroup[],
): string => {
  if (rule.event_key) return displayNameFor(groups, rule.event_key);
  if (rule.trigger_key) return segmentTriggerDisplayLabel(rule.trigger_key);
  return EMPTY;
};

/** Render a membership rule as a sentence, e.g. "When User signed up AND state eq MA". */
const describeRule = (rule: MembershipRuleOut, groups: EventCategoryGroup[]): string =>
  [`When ${eventLabel(rule, groups)}`, ...(rule.field_checks ?? []).map(describeCheck)].join(" AND ");

const Row: FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className={wizardStyles.summaryRow}>
    <p className={wizardStyles.summaryLabel}>{label}</p>
    <p className={wizardStyles.summaryValue}>{value ?? EMPTY}</p>
  </div>
);

const RuleCard: FC<{
  rule: MembershipRuleOut;
  index: number;
  eventGroups: EventCategoryGroup[];
}> = ({ rule, index, eventGroups }) => (
  <div className={styles.stepCard}>
    <div className={styles.stepCardHeader}>
      <p className={styles.stepCardTitle}>Rule {index + 1}</p>
    </div>
    <div className={styles.stepCardBody}>
      <p className={styles.ruleSentence}>{describeRule(rule, eventGroups)}</p>
      {rule.ttl_days != null && (
        <Row label={LOOKBACK_WINDOW_DAYS_LABEL} value={rule.ttl_days} />
      )}
    </div>
  </div>
);

type TransitionButton = {
  action: SegmentTransitionAction;
  label: string;
  icon: ReactNode;
};

const TRANSITIONS: Record<string, TransitionButton[]> = {
  active: [{ action: "pause", label: "Stop", icon: <StopOutlined /> }],
  paused: [{ action: "activate", label: "Run", icon: <CaretRightOutlined /> }],
  archived: [],
};

type RuleFormValue = {
  rule_id?: string;
  event_key?: string;
  field_checks?: Array<{ field?: string; operator?: FieldOperator; value?: unknown }>;
};

type EditFormValues = {
  name: string;
  description?: string;
  membership_ttl_days?: string;
  membership_rules?: RuleFormValue[];
};

const MEMBER_COLUMNS = () => [
  { title: AppStrings.USER_ID, dataIndex: "user_id", key: "user_id" },
  { title: AppStrings.PAM_DYNAMIC_BONUS_COLUMN_SOURCE, dataIndex: "source", key: "source", width: 160 },
  {
    title: AppStrings.PAM_SEGMENTS_JOINED,
    dataIndex: "joined_at",
    key: "joined_at",
    width: 180,
    render: (v: string) => fmt(v),
  },
  {
    title: AppStrings.PAM_SEGMENTS_EXPIRES,
    dataIndex: "expires_at",
    key: "expires_at",
    width: 180,
    render: (v: string | null) => fmt(v),
  },
];

const SegmentDetailPage: FC = () => {
  const navigate = useNavigate();
  const { segmentId } = useParams<{ segmentId: string }>();
  const [form] = Form.useForm<EditFormValues>();
  const [isEditing, setIsEditing] = useState(false);
  const [pageKey, setPageKey] = useState<string | undefined>(undefined);
  const [pageStack, setPageStack] = useState<string[]>([]);

  const { data: segment, isLoading, isError } = useGetSegment(segmentId ?? "");
  usePageTitle(
    isLoading
      ? AppStrings.PAM_DYNAMIC_BONUS_AUDIENCE_SEGMENT
      : (segment?.name ?? AppStrings.PAM_DYNAMIC_BONUS_AUDIENCE_SEGMENT),
  );
  const updateMutation = useUpdateSegment(segmentId ?? "");
  const transitionMutation = useSegmentTransition(segmentId ?? "");
  const membersQuery = useListSegmentMembers(segmentId ?? "", pageKey);
  const { data: campaigns, isSuccess: campaignsSuccess } = useListCampaigns();
  const { data: eventGroups = [] } = useDynamicBonusEvents();

  // `preserve: true` keeps the watched value available immediately after the
  // edit form unmounts (switching back to view mode), so the summary aside
  // never flashes stale/empty values mid-transition.
  const watchedName = Form.useWatch("name", { form, preserve: true }) as string | undefined;
  const watchedTtlDays = Form.useWatch("membership_ttl_days", { form, preserve: true }) as
    | string
    | undefined;
  const watchedRules = Form.useWatch("membership_rules", { form, preserve: true }) as
    | RuleFormValue[]
    | undefined;

  // Populated directly from the Edit button's `onClick` (below) rather than
  // an effect keyed on `segment` — `useGetSegment` refetches in the
  // background (e.g. on window focus) while the user is mid-edit, and an
  // effect would silently overwrite their in-progress changes with whatever
  // the server returns.
  const beginEdit = (s: SegmentOut) => {
    form.setFieldsValue({
      name: s.name,
      description: s.description ?? "",
      membership_ttl_days: s.membership_ttl_days != null ? String(s.membership_ttl_days) : "",
      membership_rules: (s.membership_rules ?? []).map((rule) => ({
        rule_id: rule.rule_id,
        event_key: rule.event_key ?? undefined,
        field_checks: (rule.field_checks ?? []).map((fc) => ({
          field: fc.field,
          operator: fc.operator,
          value: fc.operator === "exists" ? undefined : formatCheckValue(fc.value),
        })),
      })),
    });
    setIsEditing(true);
  };

  const commitSave = (payload: SegmentIn) => {
    updateMutation.mutate(payload, { onSuccess: () => setIsEditing(false) });
  };

  const confirmCampaignImpact = (
    payload: SegmentIn,
    impacted: Array<{ campaign_id: string; name: string }>,
  ) => {
    Modal.confirm({
      title: "Campaigns may be impacted",
      content:
        impacted.length > 0 ? (
          <div>
            <p>
              Changing this segment could impact current or future campaigns. Are you sure you want
              to save?
            </p>
            <ul>
              {impacted.map((c) => (
                <li key={c.campaign_id}>{c.name}</li>
              ))}
            </ul>
          </div>
        ) : (
          "Changing this segment could impact current or future campaigns. Are you sure you want to save?"
        ),
      okText: "Save anyway",
      cancelText: "Cancel",
      onOk: () => commitSave(payload),
    });
  };

  const handleSave = async () => {
    if (!segment) return;
    try {
      const values = await form.validateFields();
      const ttlDays =
        values.membership_ttl_days != null && values.membership_ttl_days !== ""
          ? parseInt(values.membership_ttl_days, 10)
          : null;

      // Field checks and event_key come straight from the form; rule_id and
      // per-rule ttl_days are never edited here, so they're merged back from
      // the currently loaded segment (matched by rule_id) rather than routed
      // through hidden form fields.
      const membershipRules: MembershipRuleIn[] = (values.membership_rules ?? []).map((rule) => {
        const original = rule.rule_id
          ? segment.membership_rules?.find((r) => r.rule_id === rule.rule_id)
          : undefined;
        return {
          ...(rule.rule_id ? { rule_id: rule.rule_id } : {}),
          event_key: rule.event_key!,
          field_checks: (rule.field_checks ?? []).map((fc, index) => ({
            field: fc.field!,
            operator: fc.operator!,
            ...(fc.operator === "exists"
              ? {}
              : { value: resolveCheckValue(fc, original?.field_checks?.[index]) }),
          })),
          ...(original?.ttl_days != null ? { ttl_days: original.ttl_days } : {}),
        };
      });

      const payload = toSegmentIn(segment, {
        name: values.name,
        description: values.description || undefined,
        membership_ttl_days: ttlDays,
        membership_rules: membershipRules,
      });

      // Warn when campaigns reference this segment. Skip the modal only when
      // the campaign list loaded successfully and none match; if the list
      // errored (or never resolved), still warn generically rather than
      // silently saving.
      const impacted =
        campaignsSuccess && campaigns
          ? campaignsUsingSegment(campaigns, segment.segment_id)
          : [];
      if (campaignsSuccess && impacted.length === 0) {
        commitSave(payload);
        return;
      }
      confirmCampaignImpact(payload, impacted);
    } catch {
      return;
    }
  };

  const currentEventKeysLabel = (s: SegmentOut): string => {
    const rules = isEditing ? watchedRules ?? [] : s.membership_rules ?? [];
    const keys = Array.from(
      new Set(
        rules
          .map((r) => eventLabel(r, eventGroups))
          .filter((k) => Boolean(k) && k !== EMPTY),
      ),
    );
    return keys.length > 0 ? keys.join(", ") : EMPTY;
  };

  const renderDetailsView = (s: SegmentOut) => (
    <div className={wizardStyles.summarySection}>
      <p className={styles.sectionHeader}>Details</p>
      <div className={styles.viewField}>
        <p className={styles.viewFieldLabel}>Name</p>
        <p className={styles.viewFieldValue}>{s.name}</p>
      </div>
      <div className={styles.viewField}>
        <p className={styles.viewFieldLabel}>Description</p>
        <p className={styles.viewFieldValue}>{s.description || EMPTY}</p>
      </div>
      <div className={styles.viewField}>
        <p className={styles.viewFieldLabel}>{LOOKBACK_WINDOW_DAYS_LABEL}</p>
        <p className={styles.viewFieldValue}>{s.membership_ttl_days ?? "No expiry"}</p>
      </div>
    </div>
  );

  const renderRulesView = (rules: MembershipRuleOut[]) =>
    rules.length > 0 && (
      <div className={wizardStyles.summarySection}>
        <p className={styles.sectionHeader}>Membership Rules</p>
        {rules.map((rule, i) => (
          <RuleCard key={rule.rule_id ?? i} rule={rule} index={i} eventGroups={eventGroups} />
        ))}
      </div>
    );

  const renderDetailsEdit = () => (
    <div className={wizardStyles.summarySection}>
      <p className={styles.sectionHeader}>Details</p>
      <Form.Item
        name="name"
        label="Name"
        rules={[{ required: true, message: "Name is required" }]}
      >
        <Input placeholder="Segment name" />
      </Form.Item>
      <Form.Item name="description" label="Description">
        <Input placeholder="Optional description" />
      </Form.Item>
      <Form.Item
        label={LOOKBACK_WINDOW_DAYS_LABEL}
        name="membership_ttl_days"
        getValueFromEvent={(e) => formatWholeNumberInput(e.target.value)}
        rules={[
          {
            validator: (_, value: string | undefined) =>
              !value || parseInt(value, 10) >= 1
                ? Promise.resolve()
                : Promise.reject(new Error(`${LOOKBACK_WINDOW_DAYS_LABEL} must be at least 1`)),
          },
        ]}
      >
        <Input placeholder="e.g. 30" />
      </Form.Item>
    </div>
  );

  const renderContent = (s: SegmentOut) => (
    <>
      <div className={wizardStyles.content}>
        <div className={wizardStyles.wizardCard}>
          <div className={wizardStyles.stepPanel}>
            {isEditing ? (
              <Form form={form} className={styles.form}>
                {renderDetailsEdit()}
                <div className={wizardStyles.summarySection}>
                  <p className={styles.sectionHeader}>Membership Rules</p>
                  <SegmentRulesEditor />
                </div>
                <div className={styles.stepFooter}>
                  <Button intent="primary" loading={updateMutation.isPending} onClick={handleSave}>
                    Save
                  </Button>
                  <Button intent="neutral" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                </div>
              </Form>
            ) : (
              <>
                {renderDetailsView(s)}
                {renderRulesView(s.membership_rules ?? [])}
              </>
            )}
          </div>
        </div>

        <aside className={wizardStyles.summary}>
          <div className={wizardStyles.summarySection}>
            <p className={wizardStyles.summaryTitle}>Segment</p>
            <dl className={wizardStyles.summaryList}>
              <div className={wizardStyles.summaryRow}>
                <dt className={wizardStyles.summaryLabel}>Name</dt>
                <dd className={wizardStyles.summaryValue}>
                  {(isEditing ? watchedName : s.name) || EMPTY}
                </dd>
              </div>
              <div className={wizardStyles.summaryRow}>
                <dt className={wizardStyles.summaryLabel}>{LOOKBACK_WINDOW_DAYS_LABEL}</dt>
                <dd className={wizardStyles.summaryValue}>
                  {/* `watchedTtlDays` is a form string — "" (cleared) is falsy but
                      not nullish, so `??` alone would show a blank instead of
                      "No expiry" while editing. `s.membership_ttl_days` is the
                      real `number | null` from the segment, where `??` is correct
                      (a literal `0` should still render, not fall back). */}
                  {isEditing
                    ? watchedTtlDays || "No expiry"
                    : s.membership_ttl_days ?? "No expiry"}
                </dd>
              </div>
              {!isEditing && (
                <>
                  <div className={wizardStyles.summaryRow}>
                    <dt className={wizardStyles.summaryLabel}>Status</dt>
                    <dd className={wizardStyles.summaryValue}>
                      <StatusBadge tone={segmentStatusBadgeTone(s.status)}>{s.status}</StatusBadge>
                    </dd>
                  </div>
                  <div className={wizardStyles.summaryRow}>
                    <dt className={wizardStyles.summaryLabel}>ID</dt>
                    <dd className={wizardStyles.summaryValue}>{s.segment_id}</dd>
                  </div>
                  <div className={wizardStyles.summaryRow}>
                    <dt className={wizardStyles.summaryLabel}>Created</dt>
                    <dd className={wizardStyles.summaryValue}>{fmt(s.created_at)}</dd>
                  </div>
                  <div className={wizardStyles.summaryRow}>
                    <dt className={wizardStyles.summaryLabel}>Updated</dt>
                    <dd className={wizardStyles.summaryValue}>{fmt(s.updated_at)}</dd>
                  </div>
                </>
              )}
            </dl>
          </div>
          <div className={wizardStyles.summarySection}>
            <p className={wizardStyles.summaryTitle}>Membership Rule</p>
            <dl className={wizardStyles.summaryList}>
              <div className={wizardStyles.summaryRow}>
                <dt className={wizardStyles.summaryLabel}>
                  {AppStrings.PAM_DYNAMIC_BONUS_TRIGGER_EVENT}
                </dt>
                <dd className={wizardStyles.summaryValue}>{currentEventKeysLabel(s)}</dd>
              </div>
            </dl>
          </div>
        </aside>
      </div>

      {!isEditing && (
        <div className={listStyles.tableSection}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <h2 className={styles.sectionTitle}>
              Members{membersQuery.data?.count != null ? ` (${membersQuery.data.count})` : ""}
            </h2>
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                intent="secondary"
                disabled={pageStack.length === 0}
                onClick={() => {
                  const prev = [...pageStack];
                  prev.pop();
                  setPageKey(prev[prev.length - 1]);
                  setPageStack(prev);
                }}
              >
                Previous
              </Button>
              <Button
                intent="secondary"
                disabled={!membersQuery.data?.next_page_key}
                onClick={() => {
                  const next = membersQuery.data?.next_page_key ?? undefined;
                  if (next) {
                    setPageStack((prevStack) => [...prevStack, next]);
                    setPageKey(next);
                  }
                }}
              >
                Next
              </Button>
            </div>
          </div>
          <DataTable<SegmentMember>
            rowKey="user_id"
            columns={MEMBER_COLUMNS()}
            dataSource={membersQuery.data?.items ?? []}
            loading={membersQuery.isLoading}
            pagination={false}
          />
        </div>
      )}
    </>
  );

  return (
    <>
      <FeatureFlagGuard feature="dynamicBonuses" mode="route">
        <div className={wizardStyles.page}>
          <div className={wizardStyles.header}>
            <button
              type="button"
              className={wizardStyles.backButton}
              onClick={() => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_SEGMENTS)}
              aria-label="Back to segments"
            >
              <span className={wizardStyles.backButtonIcon}><LeftOutlined /></span>
            </button>
            {segment && !isEditing && (
              <div className={styles.headerActions}>
                {(TRANSITIONS[segment.status] ?? []).map(({ action, label, icon }) => (
                  <Button
                    key={action}
                    intent="secondary"
                    iconLeft={icon}
                    loading={transitionMutation.isPending}
                    onClick={() => transitionMutation.mutate(action)}
                  >
                    {label}
                  </Button>
                ))}
                <Button
                  intent="secondary"
                  iconLeft={<EditOutlined />}
                  onClick={() => beginEdit(segment)}
                >
                  Edit
                </Button>
              </div>
            )}
          </div>

          {isLoading && <Spin />}
          {isError && <p style={{ color: "var(--red-400)" }}>Failed to load segment.</p>}
          {segment && renderContent(segment)}
        </div>
      </FeatureFlagGuard>
    </>
  );
};

export default SegmentDetailPage;
